import {ReactNode, useCallback, useEffect, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'
import {
  AlertCircle,
  Building2,
  Check,
  CheckCircle2,
  Factory,
  LayoutGrid,
  Loader2,
  Mail,
  Pencil,
  Search,
  ShieldCheck,
  Truck,
  UserPlus,
  Users,
  X,
} from 'lucide-react'
import {
  createSellerUser,
  getSellerUserOptions,
  getSellerUsers,
  SellerAccountUser,
  SellerUserOptions,
  SellerUserPayload,
  updateSellerUser,
} from '../services/sellerUsersApi'
import {CardHelp} from '../shared/components/CardHelp'

type FormState = SellerUserPayload & {seller_user_id?: number; account_role?: 'owner' | 'member'}
type UserStatusFilter = 'all' | 'active' | 'inactive'
const UNINFORMED_SUPPLIER_ID = 0
const blankForm = (): FormState => ({
  name: '', email: '', is_actived: true, screens: [],
  all_industries: true, industries: [], all_cnpjs: true, cnpjs: [], all_suppliers: true, suppliers: [],
})

function errorMessage(error: any) {
  return error?.response?.data?.error || error?.message || 'Não foi possível concluir a operação.'
}

function ScopeBlock({title, description, allLabel, all, futureAware = false, icon, options, selected, onChange, children}: {
  title: string
  description: string
  allLabel: string
  all: boolean
  futureAware?: boolean
  icon: ReactNode
  options: Array<{id: string | number; label: string}>
  selected: Array<string | number>
  onChange: (all: boolean, selected: Array<string | number>) => void
  children?: ReactNode
}) {
  const [query, setQuery] = useState('')
  const selectedIds = useMemo(() => new Set(selected.map(String)), [selected])
  const visibleOptions = useMemo(() => {
    const term = query.trim().toLocaleLowerCase('pt-BR')
    return term ? options.filter((option) => option.label.toLocaleLowerCase('pt-BR').includes(term)) : options
  }, [options, query])
  const selectedCount = all ? options.length : options.filter((option) => selectedIds.has(String(option.id))).length

  const toggleAll = () => {
    if (all) {
      onChange(false, futureAware ? options.map((option) => option.id) : [])
      return
    }
    onChange(true, futureAware ? selected : options.map((option) => option.id))
  }

  const toggleOption = (id: string | number) => {
    if (all) {
      onChange(false, options.filter((option) => String(option.id) !== String(id)).map((option) => option.id))
      return
    }
    const exists = selectedIds.has(String(id))
    onChange(false, exists ? selected.filter((item) => String(item) !== String(id)) : [...selected, id])
  }

  return <section className='flex min-h-0 flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
    <div className='border-b border-gray-100 p-5'>
      <div className='flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between'>
        <div className='flex min-w-0 gap-3'>
          <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-blue-700'>{icon}</div>
          <div className='min-w-0'>
            <h3 className='font-bold text-gray-950'>{title}</h3>
            <p className='mt-1 text-xs leading-5 text-gray-500'>{description}</p>
          </div>
        </div>
        <button
          type='button'
          role='switch'
          aria-checked={all}
          onClick={toggleAll}
          className={`inline-flex shrink-0 items-center gap-2.5 self-start rounded-xl border px-3 py-2 text-xs font-bold transition ${all ? 'border-blue-200 bg-blue-50 text-blue-700' : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'}`}
        >
          <span className={`relative h-5 w-9 rounded-full transition ${all ? 'bg-blue-600' : 'bg-gray-300'}`}>
            <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm transition ${all ? 'left-[18px]' : 'left-0.5'}`}/>
          </span>
          {allLabel}
        </button>
      </div>
      {children && <div className='mt-4'>{children}</div>}
      {!all && <div className='mt-4 flex items-start gap-2 rounded-xl bg-amber-50 px-3 py-2.5 text-xs leading-5 text-amber-700'>
        <AlertCircle className='mt-0.5 h-4 w-4 shrink-0'/>
        <span>{selectedCount} de {options.length} opções selecionadas. Apenas as opções marcadas ficarão disponíveis.</span>
      </div>}
    </div>
    <div className='flex min-h-0 flex-1 flex-col bg-gray-50/70 p-3'>
      <div className='mb-3 flex flex-col gap-2 sm:flex-row sm:items-center'>
        <div className='relative min-w-0 flex-1'>
          <Search className='absolute left-3 top-2.5 h-4 w-4 text-gray-400'/>
          <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={`Buscar em ${title.toLocaleLowerCase('pt-BR')}`} className='w-full rounded-xl border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100'/>
        </div>
        {!all && selectedCount > 0 && <button type='button' onClick={() => onChange(false, [])} className='shrink-0 rounded-lg px-2 py-2 text-xs font-semibold text-gray-500 hover:bg-white hover:text-gray-800'>Limpar seleção</button>}
      </div>
      <div className='max-h-56 min-h-[7.5rem] overflow-y-auto pr-1'>
        {options.length === 0 && <div className='flex h-28 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white px-4 text-center text-sm text-gray-500'>Nenhuma opção disponível.</div>}
        {options.length > 0 && visibleOptions.length === 0 && <div className='flex h-28 items-center justify-center rounded-xl border border-dashed border-gray-200 bg-white px-4 text-center text-sm text-gray-500'>Nenhuma opção encontrada para a busca.</div>}
        <div className='grid gap-2 sm:grid-cols-2'>
          {visibleOptions.map((option) => {
            const checked = all || selectedIds.has(String(option.id))
            return <label key={String(option.id)} className={`group flex cursor-pointer items-center gap-3 rounded-xl border p-3 text-sm transition ${checked ? 'border-blue-200 bg-white text-gray-900 shadow-sm' : 'border-transparent bg-white/70 text-gray-600 hover:border-gray-200 hover:bg-white'}`}>
              <input type='checkbox' checked={checked} onChange={() => toggleOption(option.id)} className='peer sr-only'/>
              <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${checked ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-300 bg-white text-transparent group-hover:border-blue-400'}`}><Check className='h-3.5 w-3.5' strokeWidth={3}/></span>
              <span className='min-w-0 break-words font-medium'>{option.label}</span>
            </label>
          })}
        </div>
      </div>
    </div>
  </section>
}

export function SellerUsersPage() {
  const [users, setUsers] = useState<SellerAccountUser[]>([])
  const [options, setOptions] = useState<SellerUserOptions>({screens: [], industries: [], cnpjs: [], suppliers: []})
  const [form, setForm] = useState<FormState>(blankForm())
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<UserStatusFilter>('all')
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')
  const namedSuppliers = useMemo(
    () => options.suppliers.filter((supplier) => supplier.supplier_id !== UNINFORMED_SUPPLIER_ID),
    [options.suppliers]
  )
  const canGrantUninformedSupplier = options.suppliers.some((supplier) => supplier.supplier_id === UNINFORMED_SUPPLIER_ID)
  const includesUninformedSupplier = form.all_suppliers || form.suppliers.includes(UNINFORMED_SUPPLIER_ID)

  const changeSuppliers = (all: boolean, selected: Array<string | number>) => {
    setForm((current) => ({
      ...current,
      all_suppliers: all,
      suppliers: [
        ...selected as number[],
        ...(canGrantUninformedSupplier && (current.all_suppliers || current.suppliers.includes(UNINFORMED_SUPPLIER_ID))
          ? [UNINFORMED_SUPPLIER_ID] : []),
      ],
    }))
  }

  const toggleUninformedSupplier = () => {
    setForm((current) => {
      const selected = current.all_suppliers
        ? namedSuppliers.map((supplier) => supplier.supplier_id)
        : current.suppliers.filter((supplierId) => supplierId !== UNINFORMED_SUPPLIER_ID)
      const checked = current.all_suppliers || current.suppliers.includes(UNINFORMED_SUPPLIER_ID)
      return {
        ...current,
        all_suppliers: false,
        suppliers: checked ? selected : [...selected, UNINFORMED_SUPPLIER_ID],
      }
    })
  }

  const load = useCallback(async () => {
    setLoading(true); setError('')
    try {
      const [userRows, available] = await Promise.all([getSellerUsers(), getSellerUserOptions()])
      setUsers(userRows); setOptions(available)
    } catch (err) { setError(errorMessage(err)) }
    finally { setLoading(false) }
  }, [])

  useEffect(() => { load() }, [load])
  useEffect(() => {
    if (!open) return
    const previousOverflow = document.body.style.overflow
    const closeOnEscape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !saving) setOpen(false) }
    document.body.style.overflow = 'hidden'
    window.addEventListener('keydown', closeOnEscape)
    return () => { document.body.style.overflow = previousOverflow; window.removeEventListener('keydown', closeOnEscape) }
  }, [open, saving])

  const filtered = useMemo(() => {
    const term = search.trim().toLocaleLowerCase('pt-BR')
    return users.filter((user) => {
      const matchesSearch = !term || `${user.name} ${user.email}`.toLocaleLowerCase('pt-BR').includes(term)
      const matchesStatus = statusFilter === 'all' || (statusFilter === 'active' ? user.is_actived : !user.is_actived)
      return matchesSearch && matchesStatus
    })
  }, [search, statusFilter, users])

  const edit = (user?: SellerAccountUser) => {
    setError(''); setSuccess('')
    setForm(user ? {
      seller_user_id: user.seller_user_id, account_role: user.account_role,
      name: user.name, email: user.email, is_actived: user.is_actived, screens: user.screens,
      all_industries: user.all_industries, industries: user.industries,
      all_cnpjs: user.all_cnpjs, cnpjs: user.cnpjs,
      all_suppliers: user.all_suppliers, suppliers: user.suppliers,
    } : blankForm())
    setOpen(true)
  }

  const save = async () => {
    if (form.name.trim().length < 2 || (!form.seller_user_id && !form.email?.trim())) { setError('Informe nome e e-mail.'); return }
    if (!form.screens.length) { setError('Selecione ao menos uma tela.'); return }
    if ((!form.all_industries && !form.industries.length) || (!form.all_cnpjs && !form.cnpjs.length) || (!form.all_suppliers && !form.suppliers.length)) {
      setError('Escolha ao menos uma opção para cada escopo limitado.'); return
    }
    setSaving(true); setError(''); setSuccess('')
    try {
      const payload: SellerUserPayload = {...form, name: form.name.trim(), email: form.email?.trim()}
      if (form.seller_user_id) await updateSellerUser(form.seller_user_id, payload)
      else await createSellerUser(payload)
      setOpen(false); setSuccess(form.seller_user_id ? 'Usuário atualizado.' : 'Usuário criado e convite enviado por e-mail.')
      await load()
    } catch (err) { setError(errorMessage(err)) }
    finally { setSaving(false) }
  }

  return <div className='w-full space-y-6 pb-10'>
    <div className='w-full space-y-6'>
      <div className='overflow-hidden rounded-3xl border border-blue-100 bg-gradient-to-br from-white via-white to-blue-50 shadow-sm'>
        <div className='flex flex-col gap-6 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between'>
          <div className='max-w-2xl'>
            <div className='mb-3 inline-flex items-center gap-2 rounded-full bg-blue-50 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-blue-700'><ShieldCheck className='h-4 w-4'/> Controle de acesso</div>
            <h1 className='text-3xl font-bold tracking-tight text-gray-950 sm:text-4xl'>Usuários do lojista</h1>
            <p className='mt-3 text-sm leading-6 text-gray-600 sm:text-base'>Convide sua equipe e defina, em um só lugar, as telas e os dados que cada pessoa pode acessar.</p>
          </div>
          <button onClick={() => edit()} className='inline-flex shrink-0 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-3 font-bold text-white shadow-lg shadow-blue-200 transition hover:-translate-y-0.5 hover:bg-blue-700'>
            <UserPlus className='h-5 w-5'/> Adicionar usuário
          </button>
        </div>
      </div>

      {error && !open && <div className='flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-red-700'><AlertCircle className='mt-0.5 h-5 w-5 shrink-0'/><span>{error}</span></div>}
      {success && <div className='flex items-start gap-3 rounded-2xl border border-emerald-200 bg-emerald-50 p-4 text-emerald-700'><CheckCircle2 className='mt-0.5 h-5 w-5 shrink-0'/><span>{success}</span></div>}

      <div className='grid gap-4 sm:grid-cols-3'>
        <div className='relative flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm'>
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700'><Users className='h-6 w-6'/></div>
          <div><p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Total</p><p className='mt-1 text-2xl font-bold text-gray-950'>{users.length}</p></div>
          <CardHelp content='Total de usuários vinculados a esta conta de lojista.' className='absolute right-2 top-2'/>
        </div>
        <div className='relative flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm'>
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-700'><CheckCircle2 className='h-6 w-6'/></div>
          <div><p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Ativos</p><p className='mt-1 text-2xl font-bold text-gray-950'>{users.filter((user) => user.is_actived).length}</p></div>
          <CardHelp content='Usuários ativos que podem acessar o sistema conforme suas permissões.' className='absolute right-2 top-2'/>
        </div>
        <div className='relative flex items-center gap-4 rounded-2xl border border-gray-200 bg-white p-5 shadow-sm'>
          <div className='flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-amber-50 text-amber-700'><ShieldCheck className='h-6 w-6'/></div>
          <div><p className='text-xs font-semibold uppercase tracking-wide text-gray-500'>Personalizados</p><p className='mt-1 text-2xl font-bold text-gray-950'>{users.filter((user) => !user.all_cnpjs || !user.all_industries || !user.all_suppliers).length}</p></div>
          <CardHelp content='Usuários que não possuem acesso a todos os CNPJs, indústrias ou fornecedores.' className='absolute right-2 top-2'/>
        </div>
      </div>

      <div className='w-full overflow-hidden rounded-3xl border border-gray-200 bg-white shadow-sm'>
        <div className='border-b border-gray-200 p-5 sm:p-6'>
          <div className='flex flex-col gap-5 xl:flex-row xl:items-end xl:justify-between'>
            <div>
              <div className='flex items-center gap-2'><h2 className='text-lg font-bold text-gray-950'>Pessoas com acesso</h2><span className='rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-600'>{filtered.length}</span></div>
              <p className='mt-1 text-sm text-gray-500'>Encontre uma pessoa e ajuste suas permissões rapidamente.</p>
            </div>
            <div className='flex w-full flex-col gap-3 sm:flex-row xl:w-auto'>
              <div className='inline-flex rounded-xl bg-gray-100 p-1'>
                <button onClick={() => setStatusFilter('all')} className={`rounded-lg px-3 py-2 text-xs font-bold transition ${statusFilter === 'all' ? 'bg-white text-gray-950 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Todos</button>
                <button onClick={() => setStatusFilter('active')} className={`rounded-lg px-3 py-2 text-xs font-bold transition ${statusFilter === 'active' ? 'bg-white text-emerald-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Ativos</button>
                <button onClick={() => setStatusFilter('inactive')} className={`rounded-lg px-3 py-2 text-xs font-bold transition ${statusFilter === 'inactive' ? 'bg-white text-red-700 shadow-sm' : 'text-gray-500 hover:text-gray-800'}`}>Inativos</button>
              </div>
              <div className='relative min-w-0 sm:w-80'>
                <Search className='absolute left-3 top-2.5 h-5 w-5 text-gray-400'/>
                <input value={search} onChange={(event) => setSearch(event.target.value)} placeholder='Buscar nome ou e-mail' className='w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-4 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100'/>
              </div>
            </div>
          </div>
        </div>
        {loading ? <div className='flex items-center justify-center gap-2 p-16 text-gray-500'><Loader2 className='h-5 w-5 animate-spin'/> Carregando usuários...</div> :
          <div className='divide-y divide-gray-100'>{filtered.map((user) => <div key={user.seller_user_id} className='grid gap-4 p-5 transition hover:bg-gray-50/80 sm:p-6 lg:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_auto] lg:items-center'>
            <div className='flex min-w-0 items-center gap-4'>
              <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl font-bold ${user.is_actived ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-500'}`}>{user.name.slice(0, 2).toUpperCase()}</div>
              <div className='min-w-0'>
                <div className='flex flex-wrap items-center gap-2'><p className='truncate font-bold text-gray-950'>{user.name}</p>{user.account_role === 'owner' && <span className='rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-bold text-violet-700'>Proprietário</span>}<span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${user.is_actived ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'}`}><span className={`h-1.5 w-1.5 rounded-full ${user.is_actived ? 'bg-emerald-500' : 'bg-red-500'}`}/>{user.is_actived ? 'Ativo' : 'Inativo'}</span></div>
                <p className='mt-1 truncate text-sm text-gray-500'>{user.email}</p>
              </div>
            </div>
            <div className='flex flex-wrap gap-2 text-xs font-semibold text-gray-600'>
              <span className='rounded-lg border border-gray-200 bg-white px-2.5 py-1.5'>{user.screens.length} {user.screens.length === 1 ? 'tela' : 'telas'}</span>
              <span className='rounded-lg border border-gray-200 bg-white px-2.5 py-1.5'>{user.all_cnpjs ? 'Todos os CNPJs' : `${user.cnpjs.length} CNPJs`}</span>
              <span className='rounded-lg border border-gray-200 bg-white px-2.5 py-1.5'>{user.all_suppliers ? 'Todos os fornecedores' : `${user.suppliers.length} fornecedores`}</span>
            </div>
            <button disabled={user.account_role === 'owner'} title={user.account_role === 'owner' ? 'O proprietário não pode ser editado' : 'Editar usuário'} onClick={() => edit(user)} className='inline-flex items-center justify-center gap-2 rounded-xl border border-gray-300 bg-white px-4 py-2.5 text-sm font-bold text-gray-700 transition hover:border-blue-300 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40'><Pencil className='h-4 w-4'/> Editar</button>
          </div>)}{!filtered.length && <div className='p-16 text-center text-gray-500'><div className='mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-gray-100'><Users className='h-7 w-7 text-gray-400'/></div><p className='font-semibold text-gray-700'>{users.length ? 'Nenhum usuário corresponde aos filtros.' : 'Nenhum usuário cadastrado.'}</p><p className='mt-1 text-sm'>{users.length ? 'Ajuste a busca ou o filtro de status.' : 'Adicione a primeira pessoa para começar.'}</p></div>}</div>}
      </div>
    </div>

    {open && createPortal(<div className='fixed inset-0 z-[31000] flex items-center justify-center bg-gray-950/55 p-3 backdrop-blur-[2px] sm:p-6' onMouseDown={() => !saving && setOpen(false)}>
      <div role='dialog' aria-modal='true' aria-labelledby='seller-user-modal-title' className='flex max-h-[calc(100vh-1.5rem)] w-full max-w-[90rem] flex-col overflow-hidden rounded-3xl bg-gray-50 shadow-2xl sm:max-h-[calc(100vh-3rem)]' onMouseDown={(event) => event.stopPropagation()}>
        <div className='flex flex-none items-center justify-between gap-4 border-b border-gray-200 bg-white px-5 py-4 sm:px-7 sm:py-5'>
          <div className='flex min-w-0 items-center gap-3'>
            <div className='flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-700'><UserPlus className='h-5 w-5'/></div>
            <div className='min-w-0'><h2 id='seller-user-modal-title' className='truncate text-xl font-bold text-gray-950'>{form.seller_user_id ? 'Editar usuário' : 'Adicionar usuário'}</h2><p className='mt-0.5 truncate text-sm text-gray-500'>Configure os dados da pessoa e o alcance do acesso.</p></div>
          </div>
          <button disabled={saving} onClick={() => setOpen(false)} aria-label='Fechar modal' className='rounded-xl p-2.5 text-gray-500 transition hover:bg-gray-100 hover:text-gray-900 disabled:opacity-40'><X className='h-5 w-5'/></button>
        </div>

        <div className='flex-1 overflow-y-auto'>
          <div className='w-full space-y-6 p-4 sm:p-5 lg:p-6'>
            <section className='overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm'>
              <div className='flex items-center gap-3 border-b border-gray-100 px-5 py-4'>
                <span className='flex h-7 w-7 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white'>1</span>
                <div><h3 className='font-bold text-gray-950'>Dados da pessoa</h3><p className='mt-0.5 text-xs text-gray-500'>Informações usadas para identificar e convidar o usuário.</p></div>
              </div>
              <div className='grid gap-4 p-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_16rem]'>
                <label htmlFor='seller-user-name' className='text-sm font-bold text-gray-700'>Nome completo
                  <div className='relative mt-2'><Users className='absolute left-3 top-3 h-4 w-4 text-gray-400'/><input id='seller-user-name' autoFocus={!form.seller_user_id} value={form.name} onChange={(event) => setForm({...form, name: event.target.value})} placeholder='Ex.: Maria da Silva' className='w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 font-normal outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100'/></div>
                </label>
                <label htmlFor='seller-user-email' className='text-sm font-bold text-gray-700'>E-mail
                  <div className='relative mt-2'><Mail className='absolute left-3 top-3 h-4 w-4 text-gray-400'/><input id='seller-user-email' type='email' disabled={Boolean(form.seller_user_id)} value={form.email} onChange={(event) => setForm({...form, email: event.target.value})} placeholder='nome@empresa.com.br' className='w-full rounded-xl border border-gray-300 py-2.5 pl-10 pr-3 font-normal outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-500'/></div>
                </label>
                <div className='rounded-xl border border-gray-200 bg-gray-50 p-3'>
                  <p className='text-xs font-bold uppercase tracking-wide text-gray-500'>Status do acesso</p>
                  <button type='button' role='switch' aria-checked={form.is_actived} onClick={() => setForm({...form, is_actived: !form.is_actived})} className='mt-2 flex w-full items-center justify-between gap-3 rounded-lg bg-white px-3 py-2 shadow-sm'>
                    <span className={`text-sm font-bold ${form.is_actived ? 'text-emerald-700' : 'text-gray-600'}`}>{form.is_actived ? 'Usuário ativo' : 'Usuário inativo'}</span>
                    <span className={`relative h-6 w-11 rounded-full transition ${form.is_actived ? 'bg-emerald-500' : 'bg-gray-300'}`}><span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition ${form.is_actived ? 'left-[22px]' : 'left-0.5'}`}/></span>
                  </button>
                </div>
              </div>
            </section>

            {error && <div className='flex items-start gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700'><AlertCircle className='mt-0.5 h-5 w-5 shrink-0'/><span>{error}</span></div>}

            <div className='flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between'>
              <div className='flex items-start gap-3'>
                <span className='mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white'>2</span>
                <div><h3 className='text-lg font-bold text-gray-950'>Permissões e alcance</h3><p className='mt-1 text-sm leading-6 text-gray-500'>Defina as telas e os dados que esta pessoa poderá acessar.</p></div>
              </div>
              <span className='self-start rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-bold text-gray-600 sm:self-auto'>{form.screens.length} de {options.screens.length} telas</span>
            </div>

            <div className='grid items-stretch gap-4 xl:grid-cols-2'>
              <ScopeBlock
                title='Telas visíveis'
                description='Áreas do sistema que aparecerão no menu deste usuário.'
                allLabel='Selecionar todas'
                all={form.screens.length === options.screens.length && options.screens.length > 0}
                icon={<LayoutGrid className='h-5 w-5'/>}
                options={options.screens.map((item) => ({id: item.name, label: item.label}))}
                selected={form.screens}
                onChange={(_all, selected) => setForm((current) => ({...current, screens: selected as string[]}))}
              />
              <ScopeBlock
                title='Indústrias'
                description='Indústrias cujos dados poderão ser consultados.'
                allLabel='Todas as indústrias'
                all={form.all_industries}
                futureAware
                icon={<Factory className='h-5 w-5'/>}
                options={options.industries.map((item) => ({id: item, label: item}))}
                selected={form.industries}
                onChange={(all, selected) => setForm((current) => ({...current, all_industries: all, industries: selected as string[]}))}
              />
              <ScopeBlock
                title='CNPJs'
                description='Estabelecimentos que farão parte das consultas e operações.'
                allLabel='Todos os CNPJs'
                all={form.all_cnpjs}
                futureAware
                icon={<Building2 className='h-5 w-5'/>}
                options={options.cnpjs.map((item) => ({id: item, label: item}))}
                selected={form.cnpjs}
                onChange={(all, selected) => setForm((current) => ({...current, all_cnpjs: all, cnpjs: selected as string[]}))}
              />
              <ScopeBlock
                title='Fornecedores'
                description='Fornecedores disponíveis para visualização e uso.'
                allLabel='Todos os fornecedores'
                all={form.all_suppliers}
                futureAware
                icon={<Truck className='h-5 w-5'/>}
                options={namedSuppliers.map((item) => ({id: item.supplier_id, label: item.fornecedor}))}
                selected={form.suppliers.filter((supplierId) => supplierId !== UNINFORMED_SUPPLIER_ID)}
                onChange={changeSuppliers}
              >
                {canGrantUninformedSupplier && <button
                  type='button'
                  role='switch'
                  aria-checked={includesUninformedSupplier}
                  aria-labelledby='seller-user-uninformed-supplier-label'
                  aria-describedby='seller-user-uninformed-supplier-description'
                  onClick={toggleUninformedSupplier}
                  className={`flex w-full items-center justify-between gap-4 rounded-xl border px-4 py-3 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 ${includesUninformedSupplier ? 'border-blue-200 bg-blue-50/70' : 'border-gray-200 bg-gray-50'}`}
                >
                  <span className='min-w-0'>
                    <span id='seller-user-uninformed-supplier-label' className='block text-sm font-semibold text-gray-900'>Fornecedor não identificado</span>
                    <span id='seller-user-uninformed-supplier-description' className='mt-1 block text-xs leading-5 text-gray-500'>Permitir acesso aos dados sem fornecedor vinculado.</span>
                  </span>
                  <span aria-hidden='true' className={`relative h-6 w-11 shrink-0 rounded-full transition ${includesUninformedSupplier ? 'bg-blue-600' : 'bg-gray-300'}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white shadow-sm transition ${includesUninformedSupplier ? 'left-[22px]' : 'left-0.5'}`}/>
                  </span>
                </button>}
              </ScopeBlock>
            </div>
          </div>
        </div>

        <div className='flex flex-none flex-col-reverse gap-3 border-t border-gray-200 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-7'>
          <div className='hidden items-center gap-2 text-xs text-gray-500 sm:flex'><ShieldCheck className='h-4 w-4 text-blue-600'/> As alterações entram em vigor assim que forem salvas.</div>
          <div className='flex gap-3'>
            <button disabled={saving} onClick={() => setOpen(false)} className='flex-1 rounded-xl border border-gray-300 bg-white px-5 py-2.5 font-bold text-gray-700 transition hover:bg-gray-50 disabled:opacity-50 sm:flex-none'>Cancelar</button>
            <button disabled={saving} onClick={save} className='inline-flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 font-bold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60 sm:flex-none'>{saving && <Loader2 className='h-4 w-4 animate-spin'/>} {saving ? 'Salvando...' : 'Salvar usuário'}</button>
          </div>
        </div>
      </div>
    </div>, document.body)}
  </div>
}
